class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

// p-editor-order-emitter.js (multi-handle ready, optimized)
(() => {
  const M = window.MPE || {};
  const $  = M.$  || ((sel, root=document) => root.querySelector(sel));
  const $$ = M.$$ || ((sel, root=document) => Array.from(root.querySelectorAll(sel)));
  const deps     = M.deps     || {};
  const SVGUtil  = M.svg      || {};
  const PrevUtil = M.preview  || {};

  // -------- consts / helpers --------
  const hasPub = () => typeof window.publish === 'function' && window.PUB_SUB_EVENTS;
  const EVENTS = () => window.PUB_SUB_EVENTS || {};
  const LS_LAST_BATCH = 'mpe:lastBatchId';
  const jsonFetchConfig = (method='POST') => ({
    method,
    headers:{'Content-Type':'application/json','Accept':'application/json','X-Requested-With':'XMLHttpRequest'},
    credentials:'same-origin'
  });
  const genBatchId = () => Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);

  // Unified selectors
  const SEL = {
    add: '[data-action="emit-order"]',
    remove: '[data-action="emit-order-remove"]',
    totalsHost: '[data-role="totals"]',
    totalsFallback: '.mpe-variant-table__total',
    totalsTpl: '#mpe-totals-tpl',
    totalsRowTpl: '#mpe-totals-row-tpl',
    printRadiosRoot: '.print-radios',
    table: '.mpe-variant-table',
    tableRows: '.mpe-variant-table__row[data-row-index]',
    tableFilesRow: '.mpe-variant-table__files-row',
    visibleCellInputs: '.mpe-variant-table__cell.is-visible [data-input-group]',
    filesVisibleCell: '.mpe-variant-table__files-row .mpe-variant-table__cell.is-visible'
  };

  // sections refresh fallback (HTML)
  const fetchSectionsHTML = async (sections, sectionsUrl) => {
    const url = new URL(sectionsUrl || window.location.href);
    url.searchParams.set('sections', sections.join(','));
    const resp = await fetch(url.toString(), { credentials:'same-origin', headers:{ 'Accept':'text/html' } });
    const html = await resp.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const result = {};
    sections.forEach(name => {
      const el = doc.querySelector(`#shopify-section-${name}`);
      if (el) result[name] = el.outerHTML;
    });
    return { sections: result, items: [] };
  };

  // -------- <mpe-order-emitter> --------
  class MpeOrderEmitter extends HTMLElement {
    constructor(){
      super();
      this._lastGrouped = null;
      this._recalcRAF = 0;
      this._recalcTO = 0;
    }

    connectedCallback(){
      this._onClick = this._onClick?.bind ? this._onClick.bind(this) : (e)=>this._onClick(e);
      this.addEventListener('click', this._onClick);

      this._onZonesContent = () => {
        if (this._recalcRAF) cancelAnimationFrame(this._recalcRAF);
        this._recalcRAF = requestAnimationFrame(() => {
          clearTimeout(this._recalcTO);
          this._recalcTO = setTimeout(() => this._recalcTotals(), 0);
        });
      };

      document.addEventListener('mpe:zones:content', this._onZonesContent);
      this._syncCancelVisibilityOnLoad();
      this._uiBusy(false);
      this._wireContactRequest();
    }

    disconnectedCallback(){
      this.removeEventListener('click', this._onClick);
      document.removeEventListener('mpe:zones:content', this._onZonesContent);
      if (this._recalcRAF) cancelAnimationFrame(this._recalcRAF);
    }

    _wireContactRequest(){
      const scope = this.closest('mpe-print-toggle') || document;
      const cb = scope.querySelector('[data-contact-request]');
      // (резерв під майбутню логіку)
    }

      // 1) Захист від повторних кліків поки йде запит
      _onClick(e){
        // якщо вже зайнятий — ігноруємо
        if (this.classList.contains('is-busy')) return;

        const btn = e.target.closest('[data-action]');
        if (!btn) return;
        const actions = {
          'emit-order':        () => this._handleAdd(e),
          'emit-order-remove': () => this._handleRemove(e)
        };
        actions[btn.dataset.action]?.();
      }


    // -------- group helpers (dynamic file groups) --------
    _isFileGroup(group, scope){
      try { if (window.MPE?.groups?.isFile?.(group)) return true; } catch(_) {}
      const selBase = `[data-input-group="${group}"]`;
      const vis = scope.querySelector(`${SEL.filesVisibleCell} input[type="file"]${selBase}`);
      if (vis) return true;
      const any = scope.querySelector(`input[type="file"]${selBase}`);
      return !!any;
    }

    _propNameForGroup(group){
      try {
        const scope = this.closest('mpe-print-toggle') || document;
        const el = scope.querySelector(`[data-input-group="${group}"][data-prop-alias]`);
        if (el && el.dataset.propAlias) return el.dataset.propAlias.trim();
      } catch(_) {}
      return window.MPE?.groups?.formProp?.(group) || (() => {
        const legacy = { sponsor:'_SponsorURL', vereinslogo:'_VereinslogoURL', logo:'_LogoURL' };
        const g = String(group||'').trim();
        if (legacy[g]) return legacy[g];
        const m = g.match(/^logo[\s\-_]*([0-9]+)$/i);
        if (m) return `_Logo${m[1]}URL`;
        const pascal = g.replace(/[^a-z0-9]+/gi,' ').trim().split(/\s+/).map(w=> w? w[0].toUpperCase()+w.slice(1):'').join('');
        return `_${pascal||'File'}URL`;
      })();
    }

    // -------- DOM helpers --------
    _filesRowFallback(filesRow, group){
      if (!filesRow || !group) return '';
      const key = (window.MPE && window.MPE.groups && window.MPE.groups.filesRowKey)
        ? window.MPE.groups.filesRowKey(group)
        : String(group).toLowerCase().replace(/[^a-z0-9]+/g,''); // запасний план
      return filesRow.dataset?.[`${key}Url`] || '';
    }

    _inferSideFromZone(zone=''){
      const s=String(zone).split('-')[0]||'';
      return (s==='front'||s==='back'||s==='left'||s==='right')?s:'';
    }

    _isHttpUrl(v) {
      if (!v) return false;
      const s = String(v).trim();
      if (!s) return false;
      return /^https?:\/\//i.test(s);
    }

    _pickUrlByGroup(scope, group){
      const filesRowEl = scope.querySelector(SEL.tableFilesRow) || null;
      const visInput = scope.querySelector(`${SEL.filesVisibleCell} [data-input-group="${group}"]`);
      if (visInput) {
        const dpu = visInput.dataset?.previewUrl || '';
        if (this._isHttpUrl(dpu)) return dpu;
        const val = (visInput.value || '').trim();
        if (this._isHttpUrl(val)) return val;
      }

      const fileVisible = scope.querySelector(`${SEL.filesVisibleCell} input[type="file"][data-input-group="${group}"]`);
      if (fileVisible) {
        const dpu = fileVisible.dataset?.previewUrl || '';
        if (this._isHttpUrl(dpu)) return dpu;
        const val = (fileVisible.value || '').trim();
        if (this._isHttpUrl(val)) return val;
      }

      const fb = this._filesRowFallback(filesRowEl, group) || '';
      if (this._isHttpUrl(fb)) return fb;
      return '';
    }

    _pickFileByGroup(scope, group){
      const vis = `${SEL.filesVisibleCell} input[type="file"][data-input-group="${group}"]`;
      const any = `input[type="file"][data-input-group="${group}"]`;
      const input = scope.querySelector(vis) || scope.querySelector(any);
      if (!input) return null;
      if (input.files && input.files.length > 0) return input.files[0] || null;
      return null;
    }

    _pickTextByGroup(scope, group){
      const el = scope.querySelector(`${SEL.visibleCellInputs}[data-input-group="${group}"]`);
      if (!el) return '';
      if (el.tagName.toLowerCase()==='textarea') return (el.value||'').trim();
      const type = (el.type||'').toLowerCase();
      if (type==='text' || type==='search' || type==='tel' || type==='email' || type==='url') return (el.value||'').trim();
      return (el.value||'').trim();
    }

    _getTableHandle(scope){
      const t = scope.querySelector(`${SEL.table}[data-product-handle]`);
      return t ? (t.getAttribute('data-product-handle')||'') : '';
    }

    // NEW: зібрати всі базові хендли з рядків
    _getTableHandles(scope){
      const rows = scope.querySelectorAll(SEL.tableRows);
      const set = new Set();
      rows.forEach(r => {
        const h = (r.getAttribute('data-base-handle') || '').trim();
        if (h) set.add(h);
      });
      if (set.size === 0) { // fallback на старий один
        const h0 = this._getTableHandle(scope);
        if (h0) set.add(h0);
      }
      return Array.from(set);
    }

    _collectActiveRadios(root){
      const inputs = $$('input[type="radio"][data-radio-group], input[type="checkbox"][data-radio-group]', root);
      const map = new Map();
      for (const rb of inputs) {
        if ((rb.type==='checkbox'||rb.type==='radio') && !rb.checked) continue;
        const g = (rb.dataset.radioGroup||'').trim(); if (!g) continue;
        const rec = {
          group: g,
          zone: (rb.dataset.radioZone || '').trim(),
          side: (rb.dataset.side || '').trim(),
          zone_size: (rb.dataset.zoneSize || '').trim(),
          print_product_id: rb.dataset.printProductId || '',
          print_variant_id: rb.dataset.printVariantId || '',
          print_handle: rb.dataset.printHandle || rb.dataset.productHandle || '',
          product_handle: rb.dataset.productHandle || rb.dataset.printHandle || '',
          product_price: rb.dataset.productPrice || '',
          product_title: rb.dataset.productTitle || '',
          zone_i18: (rb.dataset.radioZoneI18 || '').trim()
        };
        const arr = map.get(g) || []; arr.push(rec); map.set(g, arr);
      }
      return map;
    }

    _collectDisabledGroups(root){
      const fs = $$('.print-radios__group[data-group]', root);
      const set = new Set();
      for (const el of fs) {
        const g = el.getAttribute('data-group') || '';
        if (!g) continue;
        const cb = el.querySelector('.group-disable-toggle');
        const disabled = cb ? !!cb.checked : el.classList.contains('group-is-disabled');
        if (disabled) set.add(g);
      }
      return set;
    }

    _normalizeInputMany(el, filesRow, activeByGroup, rowCtx=null){
      const group = (el.dataset.inputGroup || '').trim();
      if (!group) return [];
      let row_key = '', variant_id_base = '';
      if (rowCtx && (rowCtx.row_key || rowCtx.variant_id_base)) {
        row_key = rowCtx.row_key || ''; variant_id_base = rowCtx.variant_id_base || '';
      } else {
        const rowEl = el.closest('.mpe-variant-table__row,[data-row-index]');
        const filesRowEl = el.closest(SEL.tableFilesRow) || null;
        if (rowEl) {
          row_key = (rowEl.getAttribute('data-key') || '').trim();
          variant_id_base = (rowEl.getAttribute('data-variant-id') || '').trim();
        } else if (filesRowEl) {
          row_key = (filesRowEl.getAttribute('data-key') || '').trim();
          variant_id_base = (filesRowEl.getAttribute('data-variant-id') || '').trim();
        }
      }
      const tag = el.tagName.toLowerCase();
      let type = (tag === 'textarea') ? 'text' : (el.type || '').toLowerCase();
      if (type === 'hidden') return [];
      let value = '', objectName = '', previewUrl = '';
      if (type === 'file') {
        objectName = el.dataset.objectName || (el.files && el.files[0] && el.files[0].name) || '';
        previewUrl = el.dataset.previewUrl || this._filesRowFallback(filesRow, group) || '';
      } else if (type === 'checkbox') {
        value = el.checked;
      } else {
        value = (el.value || '').trim();
      }

      const zoneFromInput = (el.dataset.zone || '').trim();
      if (zoneFromInput) {
        const side = (el.dataset.side || this._inferSideFromZone(zoneFromInput));
        const actArr = activeByGroup.get(group) || [];
        let meta = actArr.find(a => a.zone === zoneFromInput) || null;

        if (!meta) {
          meta = {
            print_variant_id: el.dataset.printVariantId || '',
            print_handle:     el.dataset.productHandle || el.dataset.printHandle || '',
            product_handle:   el.dataset.productHandle || el.dataset.printHandle || '',
            product_price:    el.dataset.productPrice || '',
            product_title:    el.dataset.productTitle || '',
            zone_i18:         el.dataset.radioZoneI18 || el.dataset.zoneI18 || ''
          };
        }

        return [{
          row_key, variant_id_base, group, zone: zoneFromInput, side,
          type: (type === 'textarea') ? 'text' : type,
          value, objectName, previewUrl,
          print_variant_id: meta.print_variant_id || '',
          print_handle:     meta.product_handle || meta.print_handle || '',
          print_price:      meta.product_price || '',
          print_title:      meta.product_title || '',
          zone_i18:         meta.zone_i18 || ''
        }];
      }

      const actives = activeByGroup.get(group) || [];
      if (!actives.length) return [];
      return actives.map(a => ({
        row_key, variant_id_base, group,
        zone: a.zone || '',
        side: a.side || this._inferSideFromZone(a.zone || ''),
        type: (type === 'textarea') ? 'text' : type,
        value, objectName, previewUrl,
        print_variant_id: a.print_variant_id || '',
        print_handle: a.product_handle || a.print_handle || '',
        print_price: a.product_price || '',
        print_title: a.product_title || '',
        zone_i18: a.zone_i18 || ''
      }));
    }

    _aggregateItems(entries, activeByGroup, disabledGroups){
      const byKey = new Map();
      for (const ent of entries) {
        if (!ent.group) continue;
        if (!ent.zone) continue;
        if (disabledGroups.has(ent.group)) continue;

        const key = `${ent.row_key}|${ent.group}|${ent.zone}`;
        if (!byKey.has(key)) {
          byKey.set(key, {
            id: ent.print_variant_id || null,
            quantity: 1,
            __meta: {
              handle: (ent.print_handle || '').trim(),
              title:  (ent.print_title  || '').trim(),
              price:  (ent.print_price  || '').trim(),
              side:   ent.side || '',
              group:  ent.group,
              zone:   ent.zone,
              row_key: ent.row_key
            },
            properties: {
              _Linked_key: ent.row_key,
              _Group: ent.group,
              _Zone: ent.zone,
              _Side: ent.side || ''
            }
          });
        }
        const li = byKey.get(key);
        li.properties._Typ = ent.type;

        if (ent.type === 'file') {
          if (ent.objectName) li.properties._ObjectName = ent.objectName;
          if (ent.previewUrl) li.properties._PreviewURL = ent.previewUrl;
        } else if (ent.type === 'checkbox') {
          li.properties._Value = ent.value ? '✓' : '';
        } else {
          li.properties._Value = ent.value;
        }
      }
      const items = Array.from(byKey.values());
      return { items };
    }

    _groupByPrintHandle(items){
      const g = new Map();
      const getCents = (v) => { if (v==null || v==='') return null; const n=Number(v); return Number.isFinite(n)?Math.round(n):null; };

      for (const it of items) {
        const meta = it.__meta || {};
        const handle = meta.handle || '';
        const priceCents = getCents(meta.price);
        const id = it.id || null;

        const line = {
          id,
          qty: it.quantity || 1,
          zone: meta.zone || '',
          side: meta.side || '',
          group: meta.group || '',
          row_key: meta.row_key || '',
          type: it.properties?._Typ || '',
          value: it.properties?._Value || it.properties?._ObjectName || '',
          title: meta.title || '',
          handle
        };

        if (!g.has(handle)) {
          g.set(handle, {
            print_handle: handle,
            variant_ids: new Set(id ? [id] : []),
            unit_price_cents: priceCents,
            quantity: line.qty,
            total_price_cents: priceCents!=null ? priceCents*line.qty : null,
            title: line.title || '',
            rows: [line]
          });
        } else {
          const b = g.get(handle);
          if (id) b.variant_ids.add(id);
          b.quantity += line.qty;
          b.rows.push(line);
          if (b.unit_price_cents == null && priceCents != null) b.unit_price_cents = priceCents;
          if (b.total_price_cents != null && priceCents != null) b.total_price_cents += priceCents * line.qty;
          else if (priceCents == null) b.total_price_cents = null;
          if (!b.title && line.title) b.title = line.title;
        }
      }

      const grouped = [];
      for (const [, b] of g) {
        let variant_id = null;
        if (b.variant_ids.size === 1) variant_id = Array.from(b.variant_ids)[0];
        grouped.push({
          print_handle: b.print_handle,
          title: b.title,
          variant_id,
          quantity: b.quantity,
          unit_price_cents: b.unit_price_cents,
          total_price_cents: b.total_price_cents,
          rows: b.rows
        });
      }
      return grouped;
    }

    // -------- totals (UI) --------
    _formatMoneyCents(cents){ if (cents==null) return '—'; const euros=(cents/100).toFixed(2); return euros.replace('.', ',') + ' €'; }

    _renderTotals(grouped, scope=document){
      const host = $(SEL.totalsHost, scope) || $(SEL.totalsFallback, scope); if (!host) return;
      host.innerHTML = '';
      const tpl  = $(SEL.totalsTpl, document);
      const rowT = $(SEL.totalsRowTpl, document); if (!tpl || !rowT) return;
      const root = tpl.content ? tpl.content.cloneNode(true) : document.importNode(tpl, true);
      const tbody = $('[data-role="body"]', root);
      const grandCell = $('[data-role="grand"]', root);
      let grandCents = 0, hasAnyPrice = false;

      grouped.forEach(g => {
        const row = rowT.content ? rowT.content.cloneNode(true) : document.importNode(rowT, true);
        const rTitle  = $('[data-role="title"]', row);
        const rHandle = $('[data-role="handle"]', row);
        const rQty    = $('[data-role="qty"]', row);
        const rUnit   = $('[data-role="unit"]', row);
        const rSum    = $('[data-role="sum"]', row);
        const title = g.title || '(Service)';
        const handle = g.print_handle ? `(${g.print_handle})` : '';
        const unitFmt = this._formatMoneyCents(g.unit_price_cents);
        const sumFmt  = this._formatMoneyCents(g.total_price_cents);

        if (g.total_price_cents != null) { grandCents += g.total_price_cents; hasAnyPrice = true; }
        if (rTitle)  rTitle.textContent  = title;
        if (rHandle) rHandle.textContent = handle;
        if (rQty)    rQty.textContent    = String(g.quantity);
        if (rUnit)   rUnit.textContent   = unitFmt;
        if (rSum)    rSum.textContent    = sumFmt;
        if (tbody) tbody.appendChild(row);
      });
      if (grandCell) grandCell.textContent = hasAnyPrice ? this._formatMoneyCents(grandCents) : '—';
      host.appendChild(root);
    }

    // -------- recalc from DOM --------
    _recalcTotals(){
      const scope = this.closest('mpe-print-toggle') || document;
      const printRadiosRoot = $(SEL.printRadiosRoot, scope) || scope;
      const activeByGroup   = this._collectActiveRadios(printRadiosRoot);
      const disabledGroups  = this._collectDisabledGroups(printRadiosRoot);

      const rows = $$(SEL.tableRows, scope);
      const filesRowEl = $(SEL.tableFilesRow, scope) || null;
      const filesRowVisibleInputs = filesRowEl
        ? $(`${SEL.filesVisibleCell} [data-input-group]`, scope) ? $$( `${SEL.filesVisibleCell} [data-input-group]`, scope ) : []
        : [];

      const allItems = [];
      for (const row of rows) {
        const row_key = row.getAttribute('data-key') || '';
        const variant_id_base = row.getAttribute('data-variant-id') || '';
        const rowVisibleInputs = $$(SEL.visibleCellInputs, row);

        const entriesRow = [];
        rowVisibleInputs.forEach((el) => {
          const r = this._normalizeInputMany(el, filesRowEl, activeByGroup);
          if (Array.isArray(r)) entriesRow.push(...r);
        });

        const entriesFilesForRow = [];
        filesRowVisibleInputs.forEach((el) => {
          const r = this._normalizeInputMany(el, filesRowEl, activeByGroup, { row_key, variant_id_base });
          if (Array.isArray(r)) entriesFilesForRow.push(...r);
        });

        const { items } = this._aggregateItems([...entriesRow, ...entriesFilesForRow], activeByGroup, disabledGroups);
        allItems.push(...items);
      }
      const grouped = this._groupByPrintHandle(allItems);
      this._lastGrouped = grouped;
      this._renderTotals(grouped, scope);
    }

    async _buildPreviewBlobForCart() {
      try { await (deps?.waitForLibs?.() || Promise.resolve()); } catch(_) {}

      { // combined SVG path
        const previewSvg =
          (PrevUtil.getPreviewSvg ? PrevUtil.getPreviewSvg() : null) ||
          document.querySelector('p-editor-preview .p-editor-preview__preview') ||
          document.querySelector('.p-editor-preview__preview');

        if (previewSvg) {
          try {
            await (PrevUtil.ensureAllSidesRendered ? PrevUtil.ensureAllSidesRendered() : Promise.resolve());

            let svgString = '', width = 1000, height = 1000;
            if (typeof PrevUtil.prepareExportSvg === 'function') {
              const x = await PrevUtil.prepareExportSvg(previewSvg);
              svgString = x.svgString; width = x.width; height = x.height;
            } else if (typeof SVGUtil.buildCombinedSVG === 'function') {
              const built = SVGUtil.buildCombinedSVG(previewSvg, { includeUnderlay: true, pad: 24 });
              await (SVGUtil.inlineExternalUses ? SVGUtil.inlineExternalUses(built.svg) : Promise.resolve());
              await (SVGUtil.inlineImagesToData ? SVGUtil.inlineImagesToData(built.svg) : Promise.resolve());
              svgString = new XMLSerializer().serializeToString(built.svg);
              width = built.width; height = built.height;
            }

            if (svgString) {
              const dataURLToBlob = (dataURL) => {
                const [head, body] = dataURL.split(',');
                const isBase64 = /;base64$/i.test(head);
                const byteStr = isBase64 ? atob(body) : decodeURIComponent(body);
                const mime = (head.match(/^data:(.*?);/)||[])[1] || 'image/png';
                const len = byteStr.length;
                const u8 = new Uint8Array(len);
                for (let i=0;i<len;i++) u8[i] = byteStr.charCodeAt(i);
                return new Blob([u8], { type: mime });
              };

              const renderPNG = async (scale) => {
                const outW = Math.max(1, Math.round(width  * scale));
                const outH = Math.max(1, Math.round(height * scale));
                try {
                  const Canvg = deps?.requireCanvg?.();
                  if (Canvg) {
                    const canvas = document.createElement('canvas');
                    canvas.width = outW; canvas.height = outH;
                    const ctx = canvas.getContext('2d');
                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = 'high';
                    const v = await Canvg.from(ctx, svgString, { enableRedraw:false, ignoreMouse:true, ignoreAnimation:true, useCORS:true });
                    await v.render();
                    let blob = await new Promise(r => canvas.toBlob(b => r(b), 'image/png'));
                    if (!blob) blob = dataURLToBlob(canvas.toDataURL('image/png'));
                    return blob;
                  }
                } catch(_) {}

                const blobSvg = new Blob([svgString], { type:'image/svg+xml;charset=utf-8' });
                const url = URL.createObjectURL(blobSvg);
                try {
                  const img = new Image(); img.crossOrigin = 'anonymous';
                  await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
                  const canvas = document.createElement('canvas');
                  canvas.width = outW; canvas.height = outH;
                  const ctx = canvas.getContext('2d');
                  ctx.imageSmoothingEnabled = true;
                  ctx.imageSmoothingQuality = 'high';
                  ctx.drawImage(img, 0, 0, outW, outH);
                  let blob = await new Promise(r => canvas.toBlob(b => r(b), 'image/png'));
                  if (!blob) blob = dataURLToBlob(canvas.toDataURL('image/png'));
                  return blob;
                } finally {
                  URL.revokeObjectURL(url);
                }
              };

              const LIMIT = 1_600_000;
              const MAX_EDGE = 2400;
              const MIN_EDGE = 1000;
              const DPR = Math.max(1, Math.min(window.devicePixelRatio || 1, 2));

              let scale = Math.min(1, MAX_EDGE / Math.max(width, height)) * DPR;
              scale = Math.max(scale, MIN_EDGE / Math.max(width, height));

              let blob = null;
              for (let i = 0; i < 7; i++) {
                blob = await renderPNG(scale);
                if (blob && blob.size <= LIMIT) break;
                const nextEdge = Math.max(width, height) * scale * 0.86;
                if (nextEdge < MIN_EDGE) break;
                scale *= 0.86;
              }

              if (blob) {
                console.log(`[mpe-order-emitter] preview via combined SVG (with underlay), ~${Math.round(blob.size/1024)} KB, scale=${scale.toFixed(2)}`);
                return blob;
              }
            }
          } catch (e) {
            console.warn('[mpe-order-emitter] combined SVG path failed:', e);
          }
        }
      }

      try {
        if (typeof PrevUtil?.generatePreviewBlob === 'function') {
          const b = await PrevUtil.generatePreviewBlob({ type: 'image/png' });
          if (b) {
            console.log('[mpe-order-emitter] preview via PrevUtil.generatePreviewBlob');
            return b;
          }
        }
      } catch(e){ console.warn('[mpe-order-emitter] PrevUtil.generatePreviewBlob failed:', e); }

      try {
        const pe = document.querySelector('p-editor-preview');
        if (pe && typeof pe.generatePreviewBlob === 'function') {
          const b = await pe.generatePreviewBlob({ type: 'image/png' });
          if (b) {
            console.log('[mpe-order-emitter] preview via <p-editor-preview>.generatePreviewBlob');
            return b;
          }
        }
      } catch(e){ console.warn('[mpe-order-emitter] pe.generatePreviewBlob failed:', e); }

      console.warn('[mpe-order-emitter] preview blob is null');
      return null;
    }

      async _handleAdd(){
        // запобігаємо подвійним клікам навіть якщо _onClick не перевірив
        if (this.classList.contains('is-busy')) return;

        const batchId = genBatchId();
        this.dataset.mpeBatchId = batchId;
        this._uiBusy(true);

        try {
          const scope           = this.closest('mpe-print-toggle') || document;
          const tableHandlesArr = this._getTableHandles(scope);
          const tableHandle     = tableHandlesArr[0] || ''; // legacy 1-й

          // 🆕 1) перед додаванням — прибираємо попередні сервісні рядки для цих же handle
          if (tableHandlesArr && tableHandlesArr.length) {
            await this._removeServiceItems({ handles: tableHandlesArr });
          } else if (tableHandle) {
            await this._removeServiceItems({ tableHandle });
          }

          // 2) далі — як було
          const grouped = this._lastGrouped && Array.isArray(this._lastGrouped)
            ? this._lastGrouped
            : this._collectGrouped();

          const radiosRoot      = document.querySelector('.print-radios') || scope;
          const activeByGroup   = this._collectActiveRadios(radiosRoot);
          const disabledGroups  = this._collectDisabledGroups(radiosRoot);

          const selectedGroups = Array.from(activeByGroup.keys());
          const fileGroups = selectedGroups.filter(g => this._isFileGroup(g, scope) && !disabledGroups.has(g));
          console.log('[mpe-order-emitter] file groups selected:', fileGroups);

          const vereinsnameSelected = activeByGroup.has('vereinsname');
          const lateContact = !!scope.querySelector('[data-contact-request]:checked');

          const valid = grouped.filter(g => g.variant_id && g.quantity > 0);
          if (!valid.length) {
            console.info('[mpe-order-emitter] No valid print items.');
            this._uiBusy(false);
            return;
          }

          // --- підготовка превʼю-файлу ---
          let previewFile = null;
          try {
            const blob = await this._buildPreviewBlobForCart();
            if (blob) {
              previewFile = new File([blob], `preview-${Date.now()}.png`, { type:'image/png' });
              console.log('[mpe-order-emitter] preview file prepared:', { name: previewFile.name, size: previewFile.size });
            } else {
              console.warn('[mpe-order-emitter] preview missing (blob is null)');
            }
          } catch (err) {
            console.warn('[mpe-order-emitter] preview PNG failed:', err);
          }

          // --- snapshot таблиці ---
          const tableJson = this._snapshotTable(scope);
          if (tableJson) {
            try { console.log('[mpe-order-emitter] VariantTableJSON:', JSON.parse(tableJson)); }
            catch { console.log('[mpe-order-emitter] VariantTableJSON (raw):', tableJson); }
          }

          // --- складання FormData ---
          const fd = new FormData();
          const getZonesI18ForGroup = (group) => {
            const arr = activeByGroup.get(group) || [];
            return Array.from(new Set(arr.map(a => (a.zone_i18 || '').trim()).filter(Boolean)));
          };

          valid.forEach((g, i) => {
            fd.append(`items[${i}][id]`, String(g.variant_id));
            fd.append(`items[${i}][quantity]`, String(g.quantity));
            fd.append(`items[${i}][properties][_MpeBatch]`, batchId);
            if (tableHandle) fd.append(`items[${i}][properties][_LinkedToHandle]`, tableHandle); // legacy
            if (tableHandlesArr && tableHandlesArr.length) {
              fd.append(`items[${i}][properties][_LinkedHandles]`, JSON.stringify(tableHandlesArr)); // NEW мульти
            }
            fd.append(`items[${i}][properties][_Handle]`, g.print_handle || '');

            const bucketGroups = Array.from(new Set((g.rows || []).map(r => r.group).filter(Boolean)));
            const zonesI18Union = Array.from(new Set(bucketGroups.flatMap(gr => getZonesI18ForGroup(gr))));
            if (zonesI18Union.length) {
              fd.append(`items[${i}][properties][_ZonesI18]`, JSON.stringify(zonesI18Union));
            }

            if (i === 0) {
              if (lateContact) fd.append(`items[${i}][properties][_ContactRequest]`, 'yes');
              if (previewFile) fd.append(`items[${i}][properties][_Preview]`, previewFile, previewFile.name);

              // file groups → файли або URL
              fileGroups.forEach(gname => {
                const prop = this._propNameForGroup(gname);
                if (!prop) return;
                const f   = this._pickFileByGroup(scope, gname);
                const url = this._pickUrlByGroup(scope, gname) || 'empty';
                if (f) fd.append(`items[${i}][properties][${prop}]`, f, f.name);
                else   fd.append(`items[${i}][properties][${prop}]`, url);
              });

              if (vereinsnameSelected && !disabledGroups.has('vereinsname')) {
                const vname = this._pickTextByGroup(scope, 'vereinsname') || 'empty';
                fd.append(`items[${i}][properties][_Vereinsname]`, vname);
              }

              if (tableJson) {
                fd.append(`items[${i}][properties][_VariantTableJSON]`, tableJson);
              }
            }
          });

          const hasPreviewInFD = Array.from(fd.keys()).some(k => /\[\s*properties\s*\]\[_Preview\]$/.test(k));
          console.log('[mpe-order-emitter] _Preview in FormData:', hasPreviewInFD);

          console.groupCollapsed('[mpe-order-emitter] /cart/add.js FormData (service only)');
          for (const [k, v] of fd.entries()) {
            if (v instanceof File) console.log(k, '=> File{name:', v.name, ', size:', v.size, ', type:', v.type, '}');
            else console.log(k, '=>', v);
          }
          console.groupEnd();

          // --- запит на додавання ---
          if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartRequestStart);
          }
          const resp = await fetch('/cart/add.js', { method:'POST', credentials:'same-origin', body: fd });
          if (!resp.ok) {
            const t = await resp.text().catch(()=> ''); throw new Error(`add.js failed (multipart): ${resp.status} ${t}`);
          }

          try { localStorage.setItem('mpe:lastBatchId', batchId); } catch(_){}
          this._toggleCancelButton(true);

          const parsedState = await this._refreshCartSections();
          this._publishCartEvents(parsedState);

          // 🆕 ПІСЛЯ УСПІШНОГО ДОДАВАННЯ — СКИДАЄМО КОНФІГУРАТОР
          this._resetConfiguratorState();

          // --- REDIRECT після успіху ---
          const redirectUrl = (this.dataset.redirectUrl && this.dataset.redirectUrl.trim())
            ? this.dataset.redirectUrl.trim()
            : '/collections/sale?open-cart-drawer=true';

          setTimeout(() => {
            window.location.assign(redirectUrl);
          }, 50);

        } catch (err) {
          console.error('[mpe-order-emitter] add failed:', err);
        } finally {
          // Якщо редирект не відбувся (наприклад, блокер), знімаємо "busy"
          this._uiBusy(false);
        }
      }



    _collectGrouped(){
      const scope = this.closest('mpe-print-toggle') || document;
      const printRadiosRoot = $(SEL.printRadiosRoot, scope) || scope;
      const activeByGroup   = this._collectActiveRadios(printRadiosRoot);
      const disabledGroups  = this._collectDisabledGroups(printRadiosRoot);
      const rows = $$(SEL.tableRows, scope);
      const filesRowEl = $(SEL.tableFilesRow, scope) || null;
      const filesRowVisibleInputs = filesRowEl
        ? $$( `${SEL.filesVisibleCell} [data-input-group]`, scope )
        : [];
      const allItems = [];
      for (const row of rows) {
        const row_key = row.getAttribute('data-key') || '';
        const variant_id_base = row.getAttribute('data-variant-id') || '';
        const rowVisibleInputs = $$(SEL.visibleCellInputs, row);
        const entriesRow = [];
        rowVisibleInputs.forEach((el) => {
          const r = this._normalizeInputMany(el, filesRowEl, activeByGroup);
          if (Array.isArray(r)) entriesRow.push(...r);
        });
        const entriesFilesForRow = [];
        filesRowVisibleInputs.forEach((el) => {
          const r = this._normalizeInputMany(el, filesRowEl, activeByGroup, { row_key, variant_id_base });
          if (Array.isArray(r)) entriesFilesForRow.push(...r);
        });
        const { items } = this._aggregateItems([...entriesRow, ...entriesFilesForRow], activeByGroup, disabledGroups);
        allItems.push(...items);
      }
      const grouped = this._groupByPrintHandle(allItems);
      this._lastGrouped = grouped;
      return grouped;
    }

    async _handleRemove(e){
      const btn = (e?.target ? e.target.closest(SEL.remove) : null) || this.querySelector(SEL.remove);

      const linkedFromBtn     = btn?.dataset?.linkedHandle || '';
      const linkedFromWrapper = btn?.closest('[data-linked-handle]')?.dataset?.linkedHandle || '';
      const linkedHandle      = linkedFromBtn || linkedFromWrapper || '';

      const scope = this.closest('mpe-print-toggle') || document;
      const tableHandleFallback = this._getTableHandle(scope) || '';
      const batchId = this.dataset.mpeBatchId || localStorage.getItem(LS_LAST_BATCH) || '';

      this._uiBusy(true);
      try {
        if (hasPub()) window.publish(EVENTS().cartRequestStart);
        const removed = await this._removeServiceItems({
          batchId: batchId || '',
          tableHandle: linkedHandle || tableHandleFallback
        });

        if (batchId) {
          try { localStorage.removeItem(LS_LAST_BATCH); } catch(_){}
          delete this.dataset.mpeBatchId;
        }

        this._toggleCancelButton(false);
        const parsedState = await this._refreshCartSections();
        this._publishCartEvents(parsedState);

        console.info('[mpe-order-emitter] removed service items:', removed, 'handle:', linkedHandle || tableHandleFallback);
      } catch (err) {
        console.error('[mpe-order-emitter] remove failed:', err);
        if (hasPub()) window.publish(EVENTS().cartRequestEnd);
      } finally {
        this._uiBusy(false);
      }
    }

   async _removeServiceItems({ batchId = '', tableHandle = '', handles = [] } = {}) {
  const cart = await fetch('/cart.js', {
    credentials: 'same-origin',
    headers: { 'Accept': 'application/json' }
  }).then(r => r.json());

  const targets = (cart.items || []).filter(li => {
    const p = li.properties || {};

    // 1) по batch
    if (batchId && p._MpeBatch === batchId) return true;

    // 2) по одному хендлу (legacy)
    if (tableHandle && p._LinkedToHandle === tableHandle) return true;

    // 3) по масиву хендлів (нове)
    if (handles && handles.length) {
      // a) простий кейс: у лайн-айті тільки один лінк
      if (p._LinkedToHandle && handles.includes(p._LinkedToHandle)) return true;

      // b) складніший: у лайн-айті збережений масив
      if (p._LinkedHandles) {
        try {
          const arr = JSON.parse(p._LinkedHandles);
          if (Array.isArray(arr) && arr.some(h => handles.includes(h))) {
            return true;
          }
        } catch (_) {}
      }
    }

    return false;
  });

  if (!targets.length) {
    console.info('[mpe-order-emitter] nothing to remove for handle/batch');
    return 0;
  }

  for (const li of targets) {
    await fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: li.key, quantity: 0 })
    }).then(res => res.ok ? null : res.text().then(t => Promise.reject(t)));
  }

  return targets.length;
}


    async _removeBatchFromCart(batchId){
      const cart = await fetch('/cart.js', { credentials:'same-origin', headers:{'Accept':'application/json'} }).then(r=>r.json());
      const targets = (cart.items || []).filter(li => (li.properties && li.properties._MpeBatch === batchId));
      if (!targets.length) return;
      for (const li of targets) {
        await fetch('/cart/change.js', { ...jsonFetchConfig('POST'), body: JSON.stringify({ id: li.key, quantity: 0 }) })
          .then(res => res.ok ? null : res.text().then(t=>Promise.reject(t)));
      }
    }

    _toggleCancelButton(show){
      const btn = this.querySelector(SEL.remove);
      if (!btn) return;
      // залишено прихованим; розкоментуй якщо потрібно показувати:
      // btn.classList.toggle('visually-hidden', !show);
    }

    async _syncCancelVisibilityOnLoad(){
      const batchId = localStorage.getItem(LS_LAST_BATCH);
      if (!batchId) { this._toggleCancelButton(false); return; }
      try {
        const cart = await fetch('/cart.js', { credentials:'same-origin', headers:{'Accept':'application/json'} }).then(r=>r.json());
        const exists = (cart.items || []).some(li => li.properties && li.properties._MpeBatch === batchId);
        if (exists) { this.dataset.mpeBatchId = batchId; this._toggleCancelButton(true); }
        else { localStorage.removeItem(LS_LAST_BATCH); this._toggleCancelButton(false); }
      } catch(e) { this._toggleCancelButton(false); }
    }

    _sectionsToRender(){
      return [
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '#shopify-section-cart-icon-bubble' },
        { id: 'CartDrawer',       section: 'cart-drawer',       selector: '.drawer__inner' },
        { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' }
      ];
    }

    async _refreshCartSections(){
      const sections = this._sectionsToRender().map(s => s.section);
      const sections_url = window.location.pathname + window.location.search;
      const updateUrl = window.routes?.cart_update_url || '/cart/update.js';
      try {
        const resp = await fetch(updateUrl, { ...jsonFetchConfig('POST'), body: JSON.stringify({ updates: {}, sections, sections_url }) });
        const parsed = await resp.json();
        this._renderSectionsToDOM(parsed);
        return parsed;
      } catch (e) {
        const fallback = await fetchSectionsHTML(sections, sections_url);
        this._renderSectionsToDOM(fallback);
        return fallback;
      }
    }

    _renderSectionsToDOM(parsedState){
      if (!parsedState || !parsedState.sections) return;
      this._sectionsToRender().forEach(({ id, selector, section }) => {
        const sectionHTML = parsedState.sections[section];
        if (!sectionHTML || typeof sectionHTML !== 'string') return;

        let container = document.getElementById(id);
        if (!container && section === 'cart-drawer') {
          container = document.querySelector('#CartDrawer .drawer__inner')?.closest('cart-drawer') || null;
        }
        const dom = new DOMParser().parseFromString(sectionHTML, 'text/html');
        const frag = dom.querySelector(selector);
        if (!frag) return;

        if (section === 'cart-drawer') {
          const target = document.querySelector('#CartDrawer .drawer__inner');
          if (target) target.innerHTML = frag.innerHTML;
          const drawer = document.querySelector('cart-drawer');
          if (drawer) drawer.classList.toggle('is-empty', (parsedState.items || []).length === 0);
        } else if (container) {
          const target = container.querySelector(selector) || container;
          target.innerHTML = frag.innerHTML;
        }
      });
    }

    _publishCartEvents(parsedState){
      if (!hasPub()) return;
      try {
        window.publish(EVENTS().cartUpdate, { source: 'mpe-order-emitter', cartData: parsedState });
        window.publish(EVENTS().quantityUpdate, { source: 'mpe-order-emitter' });
      } finally { window.publish(EVENTS().cartRequestEnd); }
    }

    _snapshotTable(scope){
      const table = scope.querySelector(SEL.table);
      if (!table) return '';

      // 1) активні групи → щоб знати, який handle зараз у групи
      const radiosRoot = $(SEL.printRadiosRoot, scope) || scope;
      const activeByGroup = this._collectActiveRadios(radiosRoot);
      const handleByGroup = {};
      activeByGroup.forEach((arr, g) => {
        const first = arr[0] || {};
        handleByGroup[g] = (first.product_handle || first.print_handle || '').trim();
      });

      // 2) legacy handle + всі base-handles
      const handleTable = table.getAttribute('data-product-handle') || ''; // legacy
      const handlesAll  = this._getTableHandles(scope);                    // NEW

      // 3) files-row (видимі інпути)
      const filesRowEl = scope.querySelector(SEL.tableFilesRow);
      const filesRowVisibleInputs = filesRowEl
        ? $$( `${SEL.filesVisibleCell} [data-input-group]`, scope )
        : [];

      // 4) проходимось по кожному рядку таблиці
      const rows = $$(SEL.tableRows, table).map(r => {
        const key    = r.getAttribute('data-key') || '';
        const vid    = r.getAttribute('data-variant-id') || '';
        const base_h = r.getAttribute('data-base-handle') || '';
        const sel    = SEL.visibleCellInputs;

        // 4a) інпути в самому рядку
        const rowInputs = $$(sel, r).map(inp => {
          const group  = inp.dataset.inputGroup || '';
          if (!group) return null;
          const value  = (inp.value || '').trim();
          const handle = handleByGroup[group] || '';
          return { g: group, h: handle, v: value };
        }).filter(Boolean);

        // 4b) інпути з files-row, але “прикріплені” до цього рядка
        const filesInputsForRow = filesRowVisibleInputs.map(inp => {
          const group  = inp.dataset.inputGroup || '';
          if (!group) return null;
          const value  = (inp.value || '').trim();
          const handle = handleByGroup[group] || '';
          return { g: group, h: handle, v: value };
        }).filter(Boolean);

        // обʼєднуємо
        const allInputs = [...rowInputs, ...filesInputsForRow];

        return {
          key,
          v_id: vid,
          base_h,
          inpts: allInputs
        };
      });

      try {
        return JSON.stringify({
          hdl: handleTable,
          hdls: handlesAll,
          rs: rows
        });
      }
      catch {
        return '';
      }
    }


    _getBtns(){
      return {
        add: this.querySelector(SEL.add),
        remove: this.querySelector(SEL.remove),
        spinner: this.querySelector('.loading__spinner')
      };
    }

    _uiBusy(on){
      const { add, remove, spinner } = this._getBtns();
      this.classList.toggle('is-busy', !!on);
      this.setAttribute('aria-busy', on ? 'true' : 'false');
      if (add) add.disabled = !!on;
      if (remove) remove.disabled = !!on;
      if (spinner) spinner.classList.toggle('hidden', !on);
    }
        _resetConfiguratorState() {
      const root = this.closest('mpe-print-toggle') || null;
      if (!root) return;

      // 1) радіо + чекбокси
      root.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach(inp => {
        inp.checked = false;
        inp.disabled = false;
        inp.classList.remove('p-editor-radio-is-conflict', 'p-editor-radio-is-priority-blocked');
        const label = inp.closest('label');
        if (label) {
          label.classList.remove('p-editor-radio-is-conflict', 'p-editor-radio-is-priority-blocked');
          label.removeAttribute('aria-disabled');
          if (label.title === 'Blocked by priority' || label.title === 'Zone already used in another group') {
            label.removeAttribute('title');
          }
        }
      });

      // 2) сховати всі клітинки таблиці
      root.querySelectorAll('.mpe-variant-table__cell[data-group]').forEach(el => {
        el.classList.remove('is-visible');
        el.classList.add('is-hidden');
      });

      // 3) очистити всі текстові / textarea / file інпути з data-input-group
      root.querySelectorAll('[data-input-group]').forEach(el => {
        const tag = el.tagName.toLowerCase();
        const type = (el.type || '').toLowerCase();

        if (type === 'file') {
          el.value = '';
          delete el.dataset.previewUrl;
          delete el.dataset.objectName;
          const label = el.closest('label');
          const nameEl = label ? label.querySelector('.mpe-file-name') : null;
          const previewEl = label ? label.querySelector('.mpe-file-preview') : null;
          if (nameEl) nameEl.textContent = '';
          if (previewEl) {
            previewEl.src = '';
            previewEl.style.display = 'none';
          }
        } else if (tag === 'textarea' || ['text','search','tel','email','url'].includes(type)) {
          el.value = '';
        }
      });

      // 4) очистити глобальні data-* у files-row (URL/Name), щоб не підтягувались старі логотипи
      const filesRow = root.querySelector('.mpe-variant-table__files-row');
      if (filesRow) {
        Object.keys(filesRow.dataset || {}).forEach(k => {
          if (k.endsWith('Url') || k.endsWith('Name')) {
            delete filesRow.dataset[k];
          }
        });
      }

      // 5) прибрати збережений стан MpePrintToggle з localStorage
      const storageKey = root.dataset.storageKey || 'mpe-print';
      try { localStorage.removeItem(storageKey); } catch (_) {}

      // 6) перезапустити логіку print-toggle (з free-first логікою)
      if (typeof root.reinitialize === 'function') {
        root.reinitialize();
      } else if (typeof root._showFreeOnly === 'function') {
        root._showFreeOnly();
      }

      // 7) дати знати всім слухачам, що зараз в зонах нічого немає
      try {
        document.dispatchEvent(
          new CustomEvent('mpe:zones:content', {
            detail: { inputs: [] },
            bubbles: true,
            composed: true
          })
        );
      } catch (_) {}
    }

  }

  if (!customElements.get('mpe-order-emitter')) customElements.define('mpe-order-emitter', MpeOrderEmitter);
})();

